import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField, MenuItem, Select,
  FormControl, InputLabel, IconButton, Chip, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, TablePagination, Tooltip,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useSearchParams } from 'react-router-dom';
import api from '../../config/api';

const ACTION_COLORS = {
  create: 'success',
  update: 'info',
  delete: 'error',
  ship: 'primary',
  receive: 'primary',
  cancel: 'warning',
  void: 'warning',
  invoice: 'secondary',
  approve: 'success',
  submit: 'info',
  send: 'info',
  close: 'default',
  upsert: 'info',
  reserve: 'info',
};

function fmtChanges(s) {
  try {
    const obj = JSON.parse(s || '{}');
    if (!obj || Object.keys(obj).length === 0) return '—';
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(s || '—');
  }
}

function fmtTs(ts) {
  if (!ts) return '';
  // CH returns either ISO string or '2026-05-14 22:35:01'
  return String(ts).replace('T', ' ').slice(0, 19);
}

export default function AuditLogViewer() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entityTypes, setEntityTypes] = useState([]);
  const [actions, setActions] = useState([]);

  // URL-bound filter state (survives back navigation).
  const [entityType, setEntityType] = useState(() => searchParams.get('entity_type') || '');
  const [action, setAction] = useState(() => searchParams.get('action') || '');
  const [entityId, setEntityId] = useState(() => searchParams.get('entity_id') || '');
  const [userEmail, setUserEmail] = useState(() => searchParams.get('user_email') || '');
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [fromDate, setFromDate] = useState(() => searchParams.get('from_date') || '');
  const [toDate, setToDate] = useState(() => searchParams.get('to_date') || '');
  const [page, setPage] = useState(() => parseInt(searchParams.get('page') || '0') || 0);
  const [rowsPerPage, setRowsPerPage] = useState(() => parseInt(searchParams.get('limit') || '50') || 50);

  // Load distinct values for the filter dropdowns once on mount.
  useEffect(() => {
    (async () => {
      try {
        const [et, ac] = await Promise.all([
          api.get('/api/audit-log/distinct/entity_type'),
          api.get('/api/audit-log/distinct/action'),
        ]);
        setEntityTypes(et.data?.data || []);
        setActions(ac.data?.data || []);
      } catch (_) { /* ignore — keeps filter form usable as plain text inputs */ }
    })();
  }, []);

  const loadEvents = useMemo(() => async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      if (entityType) params.entity_type = entityType;
      if (action) params.action = action;
      if (entityId) params.entity_id = entityId;
      if (userEmail) params.user_email = userEmail;
      if (search) params.search = search;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const r = await api.get('/api/audit-log', { params });
      setEvents(r.data?.events || []);
      setTotal(Number(r.data?.total || 0));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [entityType, action, entityId, userEmail, search, fromDate, toDate, page, rowsPerPage]);

  useEffect(() => {
    // Sync URL with current filters so back-button restores state.
    const sp = new URLSearchParams();
    if (entityType) sp.set('entity_type', entityType);
    if (action) sp.set('action', action);
    if (entityId) sp.set('entity_id', entityId);
    if (userEmail) sp.set('user_email', userEmail);
    if (search) sp.set('search', search);
    if (fromDate) sp.set('from_date', fromDate);
    if (toDate) sp.set('to_date', toDate);
    if (page > 0) sp.set('page', String(page));
    if (rowsPerPage !== 50) sp.set('limit', String(rowsPerPage));
    setSearchParams(sp, { replace: true });
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, action, entityId, userEmail, search, fromDate, toDate, page, rowsPerPage]);

  const handleClearFilters = () => {
    setEntityType(''); setAction(''); setEntityId(''); setUserEmail('');
    setSearch(''); setFromDate(''); setToDate(''); setPage(0);
  };

  // CSV export with the current filters applied. Backend caps at 100k rows.
  const handleExportCsv = async () => {
    try {
      const params = {};
      if (entityType) params.entity_type = entityType;
      if (action) params.action = action;
      if (entityId) params.entity_id = entityId;
      if (userEmail) params.user_email = userEmail;
      if (search) params.search = search;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await api.get('/api/audit-log/export.csv', { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.setAttribute('download', `audit-log-${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to export CSV');
    }
  };

  const activeFilterCount = [entityType, action, entityId, userEmail, search, fromDate, toDate].filter(Boolean).length;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Audit Log</Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Entity</InputLabel>
                <Select value={entityType} label="Entity"
                  onChange={(e) => { setEntityType(e.target.value); setPage(0); }}>
                  <MenuItem value=""><em>All</em></MenuItem>
                  {entityTypes.map(et => (
                    <MenuItem key={et.value} value={et.value}>{et.value} ({et.cnt})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Action</InputLabel>
                <Select value={action} label="Action"
                  onChange={(e) => { setAction(e.target.value); setPage(0); }}>
                  <MenuItem value=""><em>All</em></MenuItem>
                  {actions.map(a => (
                    <MenuItem key={a.value} value={a.value}>{a.value} ({a.cnt})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField size="small" fullWidth label="Entity ID"
                value={entityId} onChange={(e) => { setEntityId(e.target.value); setPage(0); }}
                placeholder="e.g. 4091603614" />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField size="small" fullWidth label="User"
                value={userEmail} onChange={(e) => { setUserEmail(e.target.value); setPage(0); }}
                placeholder="email contains…" />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField size="small" fullWidth type="date" label="From"
                InputLabelProps={{ shrink: true }}
                value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(0); }} />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField size="small" fullWidth type="date" label="To"
                InputLabelProps={{ shrink: true }}
                value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(0); }} />
            </Grid>
            <Grid item xs={10} md={9}>
              <TextField size="small" fullWidth label="Search changes / user / id"
                value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder="Free text in changes JSON, user email, or entity id" />
            </Grid>
            <Grid item xs={2} md={3} sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
              {activeFilterCount > 0 && (
                <Chip size="small" label={`${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} · Clear`}
                  onClick={handleClearFilters} color="primary" variant="outlined" />
              )}
              <Tooltip title="Refresh"><IconButton onClick={loadEvents}><RefreshIcon /></IconButton></Tooltip>
              <Tooltip title="Export current filter to CSV (max 100k rows)">
                <IconButton onClick={handleExportCsv}><FileDownloadIcon /></IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Card sx={{ mb: 2, bgcolor: 'error.lighter' }}><CardContent>
          <Typography color="error.dark">{error}</Typography>
        </CardContent></Card>
      )}

      <Card>
        <TableContainer>
          <Table size="small" sx={{ '& .MuiTableCell-head': { fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3 } }}>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Changes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && events.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.disabled' }}>Loading…</TableCell></TableRow>
              )}
              {!loading && events.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.disabled' }}>No audit events match these filters.</TableCell></TableRow>
              )}
              {events.map(ev => (
                <TableRow key={ev.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtTs(ev.ts)}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{ev.user_email || `#${ev.user_id || 0}`}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    <Chip label={ev.entity_type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{ev.entity_id}</TableCell>
                  <TableCell>
                    <Chip label={ev.action} size="small" color={ACTION_COLORS[ev.action] || 'default'} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 600, p: 0 }}>
                    <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 540 }}>
                          {String(ev.changes || '').slice(0, 120)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <Box component="pre" sx={{ fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap', m: 0, p: 1, bgcolor: 'grey.50', borderRadius: 0.5, maxHeight: 320, overflow: 'auto' }}>
                          {fmtChanges(ev.changes)}
                        </Box>
                        {(ev.ip_address || ev.user_agent || ev.request_id) && (
                          <Box sx={{ mt: 1, fontSize: 10, color: 'text.disabled', fontFamily: 'monospace' }}>
                            {ev.ip_address && <div>ip: {ev.ip_address}</div>}
                            {ev.request_id && <div>req: {ev.request_id}</div>}
                            {ev.user_agent && <div>ua: {ev.user_agent.slice(0, 100)}</div>}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100, 250]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Card>
    </Box>
  );
}
